import {
  HttpClient,
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AUTH } from '@core/constant/api.constants';
import { messageFromApiErrorBody, type ApiErrorBodyLike } from '@core/helpers/api-error.helper';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  filter,
  finalize,
  Observable,
  switchMap,
  take,
  throwError,
} from 'rxjs';
import { ToastService } from './toast.service';
import { AuthService } from './auth.service';

/**
 * When set to true on a request, the interceptor will NOT attempt a token
 * refresh on 401 — prevents infinite retry loops.
 */
const SKIP_AUTH_REFRESH = new HttpContextToken<boolean>(() => false);

/** Set to `true` on a request so the interceptor does NOT show an error toast. */
export const SKIP_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

function isRefreshEndpoint(url: string): boolean {
  return url.includes(AUTH.REFRESH_TOKEN);
}

function isLogoutEndpoint(url: string): boolean {
  return url.includes(AUTH.LOGOUT);
}

/** Login / password flows — never send a stored JWT on these URLs. */
function isPublicAuthRoute(url: string): boolean {
  const publicPaths = [
    '/auth/login',
    '/auth/forgot-password',
    '/auth/reset-password',
    '/auth/register',
    'auth/validate-reset-password-token',
  ];
  return publicPaths.some((path) => url.includes(path));
}

@Injectable({ providedIn: 'root' })
export class AuthInterceptor implements HttpInterceptor {
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);

  private isRefreshing = false;
  private readonly refreshDone$ = new BehaviorSubject<boolean>(false);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const isLogoutCall = isLogoutEndpoint(req.url);
    const skipBearer = isPublicAuthRoute(req.url) || isRefreshEndpoint(req.url);

    // After logout starts, drop every non-public/non-logout request so stale
    // component effects and in-flight retries cannot hit APIs.
    if (this.authService.isLogoutInProgress() && !skipBearer && !isLogoutCall) {
      return EMPTY;
    }

    // Read access token from in-memory store (not cookies)
    const token = this.authService.getAccessToken();

    // No token and not a public/refresh/logout route — drop the request.
    if (!token && !skipBearer && !isLogoutCall) {
      return EMPTY;
    }

    // Clone with withCredentials (so browser sends httpOnly cookies) and Bearer if applicable
    const authReq = req.clone({
      withCredentials: true,
      // ...(token && !skipBearer ? { setHeaders: { Authorization: `Bearer ${token}` } } : {}),
    });

    return next.handle(authReq).pipe(
      catchError((error: unknown) => {
        if (!(error instanceof HttpErrorResponse)) {
          return throwError((): unknown => error);
        }

        const isUnauthorized = error.status === 401;
        const alreadyRetried = authReq.context.get(SKIP_AUTH_REFRESH);
        const isRefreshCall = isRefreshEndpoint(authReq.url);
        const isLogoutCall = isLogoutEndpoint(authReq.url);
        const hasAccessToken = this.authService.getAccessToken() !== null;

        // After explicit logout, access token is cleared. In that state, do not
        // trigger refresh for stale/in-flight requests that fail with 401.
        if (
          isUnauthorized &&
          hasAccessToken &&
          !isRefreshCall &&
          !isLogoutCall &&
          !alreadyRetried &&
          !isPublicAuthRoute(authReq.url)
        ) {
          return this.handleAuthError(req, next);
        }

        // Logout errors are handled by the caller — skip interceptor redirect.
        if (isLogoutCall) {
          return throwError((): HttpErrorResponse => error);
        }

        if (isRefreshCall) {
          return throwError((): HttpErrorResponse => error);
        }

        if (!authReq.context.get(SKIP_ERROR_TOAST)) {
          const message = messageFromApiErrorBody(error.error as ApiErrorBodyLike, {
            includeErrorField: false,
          });
          if (message !== null && message.trim().length > 0) {
            this.toast.show(message, 'error');
          }
        }
        return throwError((): HttpErrorResponse => error);
      }),
    );
  }

  /**
   * POST refresh (browser sends httpOnly refresh cookie automatically),
   * then retry the failed request with the new access token.
   * If refresh fails, clear session and redirect to login.
   */
  private handleAuthError(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (this.authService.isLogoutInProgress()) {
      return EMPTY;
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshDone$.next(false);

      return this.http
        .post<{ access_token: string }>(AUTH.REFRESH_TOKEN, {}, { withCredentials: true })
        .pipe(
          switchMap((body): Observable<HttpEvent<unknown>> => {
            // Store new access token in memory
            this.authService.setAccessToken(body.access_token);
            this.refreshDone$.next(true);

            // Retry original request with new token
            const retry = request.clone({
              withCredentials: true,
              // setHeaders: { Authorization: `Bearer ${body.access_token}` },
              context: request.context.set(SKIP_AUTH_REFRESH, true),
            });
            return next.handle(retry);
          }),
          catchError((refreshError: unknown) => {
            // Attempt logout API (best-effort)
            this.http
              .post(AUTH.LOGOUT, {}, { withCredentials: true })
              .pipe(catchError((): Observable<never> => EMPTY))
              .subscribe();
            this.authService.rememberSessionError(refreshError);
            this.authService.clearSession();
            this.router.navigate(['/login']).catch(() => {});
            return throwError((): unknown => refreshError);
          }),
          finalize(() => {
            this.isRefreshing = false;
            this.refreshDone$.next(true);
          }),
        );
    }

    // Queue requests until refresh completes.
    return this.refreshDone$.pipe(
      filter((done) => done),
      take(1),
      switchMap(() => {
        if (this.authService.isLogoutInProgress() || this.authService.getAccessToken() === null) {
          return EMPTY;
        }
        const newToken = this.authService.getAccessToken()!;
        const retry = request.clone({
          withCredentials: true,
          setHeaders: { Authorization: `Bearer ${newToken}` },
          context: request.context.set(SKIP_AUTH_REFRESH, true),
        });
        return next.handle(retry);
      }),
    );
  }
}
